import React from "react";
import styled from "styled-components";

export interface SyncError {
  source: "Component" | "Fleet" | "Vehicle";
  label: string;
  message: string;
}

interface Props {
  errors: SyncError[];
  onClose: () => void;
}

export const SyncErrorsModal: React.FC<Props> = ({ errors, onClose }) => {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Sync Errors</ModalTitle>
          <ErrorBadge>
            {errors.length} error{errors.length !== 1 ? "s" : ""}
          </ErrorBadge>
        </ModalHeader>

        <ModalBody>
          <Intro>
            The following items failed to sync with AWS Greengrass. No changes
            were made for these items.
          </Intro>
          <ErrorList>
            {errors.map((err, i) => (
              <ErrorRow key={i}>
                <SourceBadge $source={err.source}>{err.source}</SourceBadge>
                <ErrorDetails>
                  {err.label && <ErrorLabel>{err.label}</ErrorLabel>}
                  <ErrorMessage>{err.message}</ErrorMessage>
                </ErrorDetails>
              </ErrorRow>
            ))}
          </ErrorList>
        </ModalBody>

        <ModalFooter>
          <CloseButton onClick={onClose}>Close</CloseButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 640px;
  max-width: 92vw;
  max-height: 80vh;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 18px 24px 14px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #b45309;
`;

const ErrorBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
`;

const ModalBody = styled.div`
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Intro = styled.p`
  margin: 0;
  font-size: 13px;
  color: #555;
  line-height: 1.5;
`;

const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 6px;
`;

const SourceBadge = styled.span<{ $source: SyncError["source"] }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 2px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  background: ${({ $source }) =>
    $source === "Component"
      ? "#dbeafe"
      : $source === "Fleet"
      ? "#d1fae5"
      : "#ede9fe"};
  color: ${({ $source }) =>
    $source === "Component"
      ? "#1e40af"
      : $source === "Fleet"
      ? "#065f46"
      : "#4c1d95"};
`;

const ErrorDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ErrorLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  word-break: break-word;
`;

const ErrorMessage = styled.span`
  font-size: 12px;
  color: #666;
  word-break: break-word;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.button`
  padding: 8px 22px;
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e0e0e0;
    border-color: #999;
  }
`;
